import React from 'react';

import { GainsChip, NftCard, NftCardHeroInteriorAlignment, NftCardSectionVariant } from '@pkg/components';
import { ChipColor, ChipSize } from '@pkg/components/Sections/Profile/Chip';
import { ChipMember } from '@pkg/components/Sections/Profile/Chips/ChipMember';
import { useAppSelector } from '@pkg/hooks';
import { type NftAsset, type NftValueCollected, type YatUser } from '@pkg/types/app/floorGame';

import { AssetCommonMaxWidth } from '@app/client';

import { NotificationDestination, type NotificationPortalRef } from '../NotificationPortal';

import { HackChipWrapper } from './styles';

export interface HeroProps {
    owner: YatUser;
    asset: NftAsset;
    gains?: NftValueCollected['gains'];
    compact?: boolean;
    notificationPortalRef?: NotificationPortalRef;
    onAction?(): void;
}

export function Hero({ owner, asset, gains, compact = false, notificationPortalRef, onAction }: HeroProps) {
    const showGainsAndLosses = useAppSelector((state) => state.featureFlags.features?.showGainsAndLosses);
    const primaryYat = owner.yat.primary;
    const twitterHandle = owner.twitter?.handle;

    const { videoUrl, imageUrl, title } = asset;

    return (
        <NftCard.Section variant={NftCardSectionVariant.Hero}>
            <NftCard.Hero>
                <NftCard.Hero.Header>
                    <NftCard.Hero.Header.Interior alignment={NftCardHeroInteriorAlignment.SpaceBetween}>
                        <HackChipWrapper>
                            <ChipMember
                                yat={primaryYat}
                                twitter={twitterHandle}
                                color={ChipColor.BlurDark}
                                size={compact ? ChipSize.Smaller : ChipSize.Small}
                            />
                        </HackChipWrapper>

                        {showGainsAndLosses && (
                            <HackChipWrapper>
                                <GainsChip percentage={gains} compact={compact} />
                            </HackChipWrapper>
                        )}
                    </NftCard.Hero.Header.Interior>
                </NftCard.Hero.Header>

                <NftCard.Hero.Main>
                    <NftCard.NftAsset
                        videoUrl={videoUrl}
                        imageUrl={imageUrl}
                        mashWidth={AssetCommonMaxWidth.Nft}
                        title={title}
                        onAction={onAction}
                    />
                </NftCard.Hero.Main>

                {/*
                    TODO: Consider still allowing a props API to
                    directly pass `notifications` as `children` to
                    <NotificationDestination />. This should continue
                    to work with both `children` and portalled markup.
                */}
                <NftCard.Hero.Footer>
                    <NftCard.Hero.Footer.Interior alignment={NftCardHeroInteriorAlignment.Right}>
                        <NotificationDestination ref={notificationPortalRef} />
                    </NftCard.Hero.Footer.Interior>
                </NftCard.Hero.Footer>
            </NftCard.Hero>
        </NftCard.Section>
    );
}
